import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SugarDaddyProfileForm from '@/components/forms/SugarDaddyProfileForm';
import { useAuth } from '@/context/auth';
import { updateSugarDaddyProfile, getSugarDaddyProfileDetails } from '@/services/sugar-profile.service';
import { useToast } from '@/hooks/use-toast';
import { useLanguageUtils } from '@/context/language-context';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { SugarDaddyProfile } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { FileWithPath } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

const BUCKET_NAME = 'sugar_media';

// Helper to extract file path from Supabase URL (same as SB edit)
const getPathFromUrl = (url: string): string | null => {
     try {
        const urlParts = new URL(url);
        const path = urlParts.pathname.split(`/${BUCKET_NAME}/`)[1];
        return path ? decodeURIComponent(path) : null;
    } catch (e) { return null; }
}

const EditSugarDaddyProfilePage: React.FC = () => {
  const { t } = useTranslation(['sugar', 'forms', 'common']);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getUrlWithLanguage } = useLanguageUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState<Partial<SugarDaddyProfile> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialMedia, setInitialMedia] = useState<{ photos: { url: string; name: string }[] }>({ photos: [] });
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);

  const profileId = user?.sugarDaddyProfileId;

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!profileId) {
        setError(t('sugar:error_profile_id_missing'));
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      const result = await getSugarDaddyProfileDetails(profileId);
      if (result.success && result.data) {
        setInitialData(result.data);
        setInitialMedia({
          photos: result.data.photos?.gallery?.map(url => ({ url, name: url.split('/').pop() || 'photo' })) || [],
        });
      } else {
        setError(result.error?.message || t('common:error_loading_data'));
      }
      setIsLoading(false);
    };
    fetchInitialData();
  }, [profileId, t]);

  // --- Upload Helper (Photos Only) ---
  const uploadPhotos = async (files: FileWithPath[]): Promise<{ url: string; name: string }[] | null> => {
    if (!user || files.length === 0) return [];
    // ... (upload logic identical to create page) ...
    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const uniqueFileName = `${uuidv4()}.${fileExt}`;
      const filePath = `public/${user.id}/photos/${uniqueFileName}`;
      const { error: uploadError } = await supabase.storage.from(BUCKET_NAME).upload(filePath, file);
      if (uploadError) { throw new Error(`Upload failed: ${uploadError.message}`); }
      const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);
      return { url: urlData.publicUrl, name: file.name };
    });
    try { return await Promise.all(uploadPromises); } catch (err) { setError((err as Error).message); return null; }
  };
  
  // --- Delete Helper (same as SB edit) ---
  const deleteStoredFiles = async (pathsToDelete: string[]) => {
    if (pathsToDelete.length === 0) return true;
    try {
      const { error: deleteError } = await supabase.storage.from(BUCKET_NAME).remove(pathsToDelete);
      if (deleteError) { toast({ title: t('common:error_deleting_files'), description: deleteError.message, variant: 'warning' }); }
      return true;
    } catch (err) { toast({ title: t('common:error_deleting_files'), description: (err as Error).message, variant: 'warning' }); return false; }
  };

  // --- Submit Handler ---
  const handleSubmit = async (formData: any, filesToUpload: FileWithPath[]) => {
    if (!profileId || !initialData) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Delete photos marked for deletion
      await deleteStoredFiles(filesToDelete);

      // 2. Upload new photos
      const uploadedPhotos = await uploadPhotos(filesToUpload);
      if (uploadedPhotos === null) {
        setIsSubmitting(false);
        return; // Upload error occurred
      }

      // 3. Merge kept and new photo URLs
      const keptPhotoUrls = initialMedia.photos
        .filter(f => !filesToDelete.includes(getPathFromUrl(f.url) ?? '--invalid--'))
        .map(f => f.url);
      const finalPhotoGallery = [...keptPhotoUrls, ...uploadedPhotos.map(p => p.url)];

      // 4. Prepare profile update data
      const profileUpdates = {
        ...formData,
        photos: finalPhotoGallery.length > 0 
          ? { ...(initialData.photos || {}), gallery: finalPhotoGallery }
          : null,
      };

      // 5. Update profile
      const result = await updateSugarDaddyProfile(profileId, profileUpdates);

      if (result.success) {
        toast({ title: t('forms:success_profile_updated') });
        navigate(getUrlWithLanguage('/sugar-relationships'));
      } else {
        setError(result.error?.message || t('common:error_unknown'));
        toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
      }
    } catch (err: any) {
      console.error("Submission error:", err);
      setError(err.message || t('common:error_unexpected'));
      toast({ title: t('common:error_title'), description: error, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
      setFilesToDelete([]);
    }
  };

  // Callback for MediaUpload to mark files for deletion
  const handleInitialFileRemoved = (removedUrl: string) => {
    const path = getPathFromUrl(removedUrl);
    if (path && !filesToDelete.includes(path)) {
      console.log('Marking for deletion:', path); // Log for debugging
      setFilesToDelete([...filesToDelete, path]);
      // Update initialMedia state to visually remove the preview
      setInitialMedia(prev => ({
        photos: prev.photos.filter(p => p.url !== removedUrl),
      }));
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 max-w-2xl">
        <h1 className="text-2xl font-bold mb-6">{t('sugar:title_edit_sd_profile')}</h1>
        {error && (
           <Alert variant="destructive" className="mb-4">
             <Terminal className="h-4 w-4" />
             <AlertTitle>{t('common:error_title')}</AlertTitle>
             <AlertDescription>{error}</AlertDescription>
           </Alert>
        )}
        {isLoading && <Skeleton className="w-full h-64" />}
        {!isLoading && initialData && (
            <SugarDaddyProfileForm 
                onSubmit={handleSubmit} 
                isSubmitting={isSubmitting} 
                initialData={initialData} 
                initialMedia={initialMedia}
                onInitialFileRemoved={handleInitialFileRemoved}
            />
        )}
      </div>
    </Layout>
  );
};

export default EditSugarDaddyProfilePage; 